/**
 * Authoritative playback-state reconciliation — sync UI play/pause/stopped
 * from renderer GetTransportInfo without wall-clock position synthesis.
 */

export type RendererTransportState =
  | 'PLAYING'
  | 'PAUSED_PLAYBACK'
  | 'STOPPED'
  | 'NO_MEDIA_PRESENT'
  | 'TRANSITIONING'
  | string;

export type LocalPlaybackState = 'playing' | 'paused' | 'stopped' | 'loading' | 'idle' | string;

export type ReconcilePlaybackStateInput = {
  localState: LocalPlaybackState;
  rendererState: RendererTransportState;
  playUriRecentMs: boolean;
  inPlayUriSilenceWindow: boolean;
  rendererHasStream: boolean;
};

export type ReconcilePlaybackStateResult =
  | { action: 'none' }
  | { action: 'set'; state: 'playing' | 'paused' | 'stopped' };

/** Map renderer transport XML state → store playbackState when they diverge. */
export function reconcilePlaybackStateFromRenderer(
  input: ReconcilePlaybackStateInput,
): ReconcilePlaybackStateResult {
  const {
    localState,
    rendererState,
    playUriRecentMs,
    inPlayUriSilenceWindow,
    rendererHasStream,
  } = input;

  if (localState === 'idle' && rendererState !== 'PLAYING' && rendererState !== 'TRANSITIONING') {
    return { action: 'none' };
  }

  if (rendererState === 'PLAYING' || rendererState === 'TRANSITIONING') {
    if (localState === 'playing' || localState === 'loading') return { action: 'none' };
    if (playUriRecentMs) return { action: 'none' };
    return { action: 'set', state: 'playing' };
  }

  if (rendererState === 'PAUSED_PLAYBACK') {
    if (localState === 'paused') return { action: 'none' };
    if (inPlayUriSilenceWindow) return { action: 'none' };
    if (
      rendererHasStream
      && (localState === 'playing' || localState === 'loading')
    ) {
      return { action: 'none' };
    }
    return { action: 'set', state: 'paused' };
  }

  if (rendererState === 'STOPPED' || rendererState === 'NO_MEDIA_PRESENT') {
    if (localState === 'stopped' || localState === 'idle') return { action: 'none' };
    if (playUriRecentMs) return { action: 'none' };
    return { action: 'set', state: 'stopped' };
  }

  return { action: 'none' };
}

export function reconcileFailureMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return String(err ?? 'unknown reconcile error');
}

export function isReconcileBuilderError(message: string): boolean {
  return /builder error/i.test(String(message || ''));
}

const _loggedReconcileFailures = new Set<string>();

export function resetReconcileFailureLogForTests(): void {
  _loggedReconcileFailures.clear();
}

/** Log the actual error once. Returns true on the first sighting of this message. */
export function logReconcileFailureOnce(
  message: string,
  extra?: Record<string, unknown>,
): boolean {
  const key = String(message || 'unknown').slice(0, 160);
  if (_loggedReconcileFailures.has(key)) return false;
  _loggedReconcileFailures.add(key);
  console.error('[NowPlayingReconcile] failed — halting background poller:', message, extra || {});
  return true;
}
